import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  JSX,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { cloneElement, isValidElement, useId } from "react";
import { Link } from "react-router";

import InfoTooltip from "@/components/common/InfoTooltip";
import { adminUiCopy } from "@/copy/adminUiCopy";
import { cx } from "@/vnext/shared/cx";

type ButtonTone = "primary" | "secondary" | "ghost" | "quiet";

interface VNextButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: ButtonTone;
  block?: boolean;
  children: ReactNode;
}

export const VNextButton = ({
  tone = "secondary",
  block = false,
  className,
  type = "button",
  children,
  ...props
}: VNextButtonProps) => (
  <button
    className={cx(
      "vnext-button",
      `vnext-button--${tone}`,
      block && "vnext-button--block",
      className,
    )}
    type={type}
    {...props}
  >
    {children}
  </button>
);

interface VNextSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Accessible label for the switch (required — there is no visible label). */
  label: string;
  disabled?: boolean;
  className?: string;
}

/**
 * On/off toggle switch. A `role="switch"` button so it is keyboard- and
 * screen-reader-accessible; style lives in `vnext.css` (`.vnext-switch`).
 */
export const VNextSwitch = ({
  checked,
  onChange,
  label,
  disabled = false,
  className,
}: VNextSwitchProps) => (
  <button
    aria-checked={checked}
    aria-label={label}
    className={cx("vnext-switch", checked && "vnext-switch--on", className)}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    role="switch"
    type="button"
  >
    <span aria-hidden="true" className="vnext-switch__thumb" />
  </button>
);

interface VNextCardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title?: ReactNode;
  titleLevel?: 2 | 3 | 4 | 5 | 6;
  titleRef?: Ref<HTMLHeadingElement>;
  titleTabIndex?: number;
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}

export const VNextCard = ({
  title,
  titleLevel = 2,
  titleRef,
  titleTabIndex,
  eyebrow,
  subtitle,
  actions,
  className,
  children,
  ...props
}: VNextCardProps) => {
  const TitleTag = `h${titleLevel}` as const;

  return (
    <section className={cx("vnext-card", className)} {...props}>
      {/* Omit the header when it would be empty so simple content blocks
            do not pick up decorative chrome with no information. */}
      {[title, subtitle, actions, eyebrow].some(
        (value) => value !== null && value !== undefined,
      ) ? (
        <header className="vnext-card__header">
          <div className="vnext-card__heading">
            {eyebrow ? (
              <span className="vnext-card__eyebrow">{eyebrow}</span>
            ) : null}
            {title ? (
              <TitleTag
                className="vnext-card__title"
                ref={titleRef}
                tabIndex={titleTabIndex}
              >
                {title}
              </TitleTag>
            ) : null}
            {subtitle ? (
              <p className="vnext-card__subtitle">{subtitle}</p>
            ) : null}
          </div>
          {actions ? (
            <div className="vnext-card__actions">{actions}</div>
          ) : null}
        </header>
      ) : null}
      <div className="vnext-card__body">{children}</div>
    </section>
  );
};

interface VNextFieldProps {
  label: ReactNode;
  hint?: string;
  error?: string | null;
  infoTitle?: string;
  infoDescription?: ReactNode;
  children: ReactNode;
}

export const VNextField = ({
  label,
  hint,
  error,
  infoTitle,
  infoDescription,
  children,
}: VNextFieldProps) => {
  const controlId = useId();
  const hintId = useId();
  const errorId = useId();
  const describedBy = error ? errorId : hint ? hintId : undefined;
  let resolvedControlId = controlId;

  let renderedChildren = children;
  if (isValidElement(children)) {
    const childProps = children.props as {
      id?: string;
      "aria-describedby"?: string;
    };
    resolvedControlId = childProps.id ?? controlId;
    const ariaDescribedBy = [childProps["aria-describedby"], describedBy]
      .filter(Boolean)
      .join(" ");

    // Preserve any caller-provided ids/ARIA wiring while appending the
    // field-level hint or error relationship generated by this wrapper.
    renderedChildren = cloneElement(
      children as JSX.Element,
      {
        id: resolvedControlId,
        ...(ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : {}),
      } as Record<string, unknown>,
    );
  }

  return (
    <div className="vnext-field">
      <span className="vnext-field__label-row">
        <label className="vnext-field__label" htmlFor={resolvedControlId}>
          {label}
        </label>
        {infoDescription ? (
          // Tooltip copy is optional supporting context, not part of
          // the core label text, so the control name stays concise.
          <InfoTooltip
            ariaLabel={
              infoTitle
                ? `${adminUiCopy.common.helpAriaPrefix}: ${infoTitle}`
                : adminUiCopy.common.moreInformation
            }
            description={infoDescription}
            title={infoTitle ?? adminUiCopy.common.moreInformation}
          />
        ) : null}
      </span>
      {renderedChildren}
      {error ? (
        <span className="vnext-field__error" id={errorId}>
          {error}
        </span>
      ) : null}
      {!error && hint ? (
        <span className="vnext-field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
};

export const VNextInput = ({
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement>) => (
  <input className={cx("vnext-input", className)} {...props} />
);

export const VNextSelect = ({
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) => (
  <select className={cx("vnext-select", className)} {...props}>
    {children}
  </select>
);

export const VNextTextArea = ({
  className,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea className={cx("vnext-textarea", className)} {...props} />
);

interface VNextPillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: "default" | "accent" | "success" | "warning" | "danger";
  children: ReactNode;
}

export const VNextPill = ({
  tone = "default",
  className,
  children,
  ...props
}: VNextPillProps) => (
  <span
    className={cx("vnext-pill", `vnext-pill--${tone}`, className)}
    {...props}
  >
    {children}
  </span>
);

interface VNextTabLinkProps {
  active?: boolean;
  children: ReactNode;
  to: string;
}

export const VNextTabLink = ({
  active = false,
  children,
  to,
}: VNextTabLinkProps) => (
  <Link className={cx("vnext-tab-link", active && "is-active")} to={to}>
    {children}
  </Link>
);

interface VNextStatProps {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
}

export const VNextStat = ({ label, value, detail }: VNextStatProps) => (
  <div className="vnext-stat">
    <div className="vnext-stat__label">{label}</div>
    <div className="vnext-stat__value">{value}</div>
    {detail ? <div className="vnext-stat__detail">{detail}</div> : null}
  </div>
);

interface VNextKeyValueListProps {
  items: Array<{
    key?: string;
    label: ReactNode;
    value: ReactNode;
  }>;
}

export const VNextKeyValueList = ({ items }: VNextKeyValueListProps) => (
  <dl className="vnext-key-value-list">
    {items.map((item, index) => (
      <div
        className="vnext-key-value-list__row"
        key={item.key ?? `row-${index}`}
      >
        <dt>{item.label}</dt>
        <dd>{item.value}</dd>
      </div>
    ))}
  </dl>
);

interface VNextNoticeProps extends HTMLAttributes<HTMLDivElement> {
  tone?: "info" | "warning" | "danger";
  action?: ReactNode;
  children: ReactNode;
}

export const VNextNotice = ({
  tone = "info",
  action,
  className,
  children,
  ...props
}: VNextNoticeProps) => (
  <div
    className={cx("vnext-notice", `vnext-notice--${tone}`, className)}
    {...props}
  >
    <div className="vnext-notice__content">{children}</div>
    {action ? <div className="vnext-notice__action">{action}</div> : null}
  </div>
);

export { VNextDialog } from "./VNextDialog";
